import React from 'react'
import { Notification } from '@offerzen/pinata'

import styles from './styles'

const TermsOfService = () => (
  <>
    <h2 id="terms-of-service" data-level="0" className={styles.heading}>Terms of Service</h2>

    <section className={styles.section}>
      <h3 id="definitions" data-level="1" className={styles.subHeading}>Definitions</h3>

      <Notification className={styles.notification} theme="dark">
        <p className={styles.noMargin}>
          💡 We use certain words repeatedly in these Terms and this section
          defines those words in a simple way. When a word is capitalised in
          these Terms, it will have a corresponding definition listed here.
        </p>
      </Notification>

      <dl className={styles.descriptionList}>
        <dt><b>“Candidate”</b></dt>
        <dd>
          &nbsp;means a prospective employee seeking employment opportunities
          through our Platform
        </dd>

        <dt><b>“Effective Date”</b></dt>
        <dd>
          &nbsp;means the date you use the Service, create an account with us,
          and/or use or licence the Service or any part of the Service
        </dd>

        <dt><b>“Employer”</b></dt>
        <dd>
          &nbsp; means companies that have signed up to the Platform with the
          intention to hire Candidates and/or the individuals who represent
          those companies
        </dd>

        <dt><b>“Identification”</b></dt>
        <dd>
          &nbsp;means the act of clicking on a Candidate to view the Candidate’s details, and/or viewing a Candidate{' '}
          on a list or card through the Employer’s profile on the Platform
        </dd>

        <dt><b>“Interview Request”</b></dt>
        <dd>
          &nbsp;means a non-binding expression of interest sent via the Platform from an Employer to a Candidate
        </dd>

        <dt><b>“Job Offer”</b></dt>
        <dd>
          &nbsp;is a formal, binding offer of employment directed by an Employer to a Candidate
        </dd>

        <dt>&nbsp;<b>“OfferZen”</b>,</dt>
        <dt>&nbsp;<b>“us”</b>,</dt>
        <dt>&nbsp;<b>“we”</b>, and</dt>
        <dt>&nbsp;<b>“our”</b>,</dt>
        <dd>
          &nbsp;means collectively, as is appropriate in the context of the use
          of the words:
          <ul className={styles.noMargin}>
            <li>OfferZen B.V. (for Services provided in Europe and generally outside of South Africa)</li>
            <li>GitFetch (Pty) Ltd., OfferZen Premium (Pty) Ltd. or OfferZen Recruitment (Pty) Ltd. (for Services provided in South Africa)</li>
            <li>Our Website, Platform and Services</li>
          </ul>
        </dd>

        <dt><b>“Platform”</b></dt>
        <dd>
          &nbsp;means OfferZen’s software as a service offering of a technical
          talent marketplace on our Website
        </dd>

        <dt><b>“Proposal”</b></dt>
        <dd>
          &nbsp;means a document and/or written offer provided to the Employer
          by OfferZen which includes additional terms relating to the Employer’s
          subscription for the Services including but not limited to: Employer
          details, subscription fees and subscription period
        </dd>

        <dt><b>“Service”</b></dt>
        <dd>&nbsp;means the Website, Platform and any products offered by OfferZen through these channels</dd>

        <dt><b>“Terms”</b></dt>
        <dd>&nbsp;means these terms of use</dd>

        <dt><b>“Website”</b></dt>
        <dd>&nbsp;means our website <a href="https://www.offerzen.com" target="_blank" rel="noreferrer">www.offerzen.com</a></dd>
      </dl>
    </section>

    <section>
      <h3 id="offerzen-service" data-level="1" className={styles.subHeading}>OfferZen’s Services</h3>

      <Notification theme="dark" className={styles.notification}>
        <p>
          💡 OfferZen provides you with access to an online marketplace of
          candidates who are actively interviewing. It’s important to note that
          we don’t perform background checks on candidates. We occasionally
          offer additional programmes which you may have the option to
          participate in.
        </p>
      </Notification>

      <p>
        OfferZen provides Employers with access to our Platform and an online
        marketplace of Candidates who are actively seeking employment.
      </p>

      <p>
        As an Employer, you acknowledge that our Services do not include performing background checks on Candidates and that{' '}
        you are responsible for performing due diligence on all Candidates including, but not limited to, background checks,{' '}
        reference checks and checks for the accuracy of Candidate provided information.
      </p>

      <p>
        OfferZen also offers participation in certain programmes when available.
        You may or may not be eligible to participate in an OfferZen programme
        when offered. Terms and conditions applicable to an OfferZen programme
        will be provided to you and you can decide whether you would like to
        participate in such a programme or not. OfferZen reserves the right to
        limit application to and/or for participation in any OfferZen Programme
        at its discretion.
      </p>

      <p>
        The Employer shall not attempt to copy, modify, duplicate, create
        derivative works from, frame, mirror, republish, download, display,
        transmit, or distribute all or any portion of the Service in any form or
        media or by any means; or attempt to de-compile, reverse compile,
        disassemble, reverse engineer or otherwise reduce to human-perceivable
        form all or any part of the Service; or access all or any part of the
        Service in order to build a product or service which competes with the
        Service; or attempt to obtain, or assist third parties in obtaining,
        access to the Service.
      </p>

      <p>
        The Employer agrees that the Service is not transferable to another
        person (including any affiliates and/or related entities) and is
        purchased solely for the use of the Employer (unless otherwise agreed
        between the Parties in the Proposal). The Service shall only be utilised
        by the Employer and its authorised employees and representatives for the
        purpose of hiring for technical roles in the Employer’s business. The
        Employer shall not use the Service for the purpose of providing similar
        services to any other person.
      </p>
    </section>

    <section>
      <h3 id="account-and-responsibilities" data-level="1" className={styles.subHeading}>Your Account, Roles and Responsibilities</h3>

      <Notification theme="dark" className={styles.notification}>
        <p>
          💡 To use OfferZen’s services you’ll need to register an account. Once
          your account is active, you’ll have access to our candidate list. Your
          first communication to a chosen candidate must be an interview request
          via the platform. If you make a hire outside of an active subscription
          period or other period of use, we will be entitled to a fee for that
          hire. Please don’t circumvent our process; if you do, you’ll have to
          pay a penalty fee.
        </p>
      </Notification>

      <p>
        You’ll need to register an account with us to use the Service. In doing
        so, you must provide us with information on the person who will be the
        point of contact for these Terms, such as their email address and name,
        so our OfferZen account managers can assist you.
      </p>

      <p>
        Account passwords or other user identification log in details must be treated as confidential. If you know or suspect that{' '}
        someone else is using your user account password or log-in details, please notify your account manager.
      </p>

      <p>
        Subject to these Terms, you retain ultimate administrative control over your account and the content within it.
      </p>

      <p>
        After your registration as an Employer has been accepted by OfferZen,
        you will be able to browse Candidates on the Platform and start to
        submit Interview Requests.
      </p>

      <p>
        Once you have Identified a Candidate on OfferZen, you agree to initiate communication with the Candidate exclusively via{' '}
        an Interview Request. The Employer and the Candidate may use other means of communication only once the Candidate has{' '}
        responded to the Interview Request.
      </p>

      <p>
        If a Candidate accepts a Job Offer, you agree to inform OfferZen within
        5 (five) business days of the Candidate’s acceptance and provide us with
        the details of the Job Offer in writing, including the Start Date,
        remuneration and duration. A Job Offer may be for any form of employment
        including temporary, part-time employment or contracting.
      </p>

      <p>
        If a Candidate Identified on OfferZen is hired by the Employer on a date
        falling outside of an active subscription, OfferZen will
        be entitled to a fee of 12.5% (twelve point five percent) of the gross
        annual salary/ total cost to company or the local equivalent (“Salary”)
        of the relevant Candidate. Salary shall include the total annual
        remuneration package of a Candidate, inclusive of basic salary,
        reimbursements, allowances, benefits, bonuses and payment in lieu of
        holiday / vacation days, all before any deductions or taxes.
      </p>

      <p>
        The fees above will not be charged if the Employer can prove that the
        Employer had already interacted with the Candidate before Identifying
        the Candidate on our Platform, and the outcome of that interaction
        didn’t result in you rejecting the Candidate. Interactions include
        having an interview, receiving a resume following a job posting, or
        having a conversation via phone, text, email or via another platform.
        The fees will also not be charged if you hire a Candidate Identified on
        the Platform more than 6 (six) months after the expiry or termination of
        the Agreement.
      </p>
    </section>

    <section>
      <h3 id="payments" data-level="1" className={styles.subHeading}>Payments</h3>

      <Notification theme="dark" className={styles.notification}>
        <p>
          💡 Payment of fees must be made by electronic funds transfer,
          according to the Proposal and our invoices. Interest may be charged on
          late payments.
        </p>
      </Notification>

      <p>
        Payments must be made by way of an electronic funds transfer into the
        OfferZen bank account as detailed in the relevant invoice/s and in
        accordance with the payment terms in the invoice/s.
      </p>

      <p>
        All fees and payments are exclusive of payable taxes or duties and you
        assume the responsibility for paying these as required. Fees payable to
        OfferZen are non-transferable.
      </p>

      <p>
        If payment is not made to OfferZen when due, OfferZen may charge
        interest on the overdue amount at a maximum of the local Prime Rate plus
        2% (two percent) per month. OfferZen reserves the right to suspend
        users’ access to the platform until outstanding fees are paid in full.
      </p>
    </section>

    <section>
      <h3 id="general" data-level="1" className={styles.subHeading}>General</h3>

      <Notification theme="dark" className={styles.notification}>
        <p>
          💡 We provide our services without any guarantee. Each party may
          cancel the Agreement for cause at any time. You own all the data and
          content you provide us with, and we’ll only use and share your data to
          provide you with our service. We take your privacy seriously and will
          treat your information with respect. You agree to keep all information
          you gain using our services secure and confidential.
        </p>
        <p>
          Our terms are governed by the laws of the Netherlands and the Republic of South
          Africa. OfferZen is indemnified by you against claims resulting from
          your use of our services.
        </p>
      </Notification>

      <h4 id="cancellation" data-level="2" className={styles.subSubHeading}>Cancellation</h4>

      <p>
        Each party has the right to terminate the Agreement without cause by
        giving an advance notice of 30 (thirty) days unless otherwise agreed in
        writing. Notice of termination must always be in writing.
      </p>

      <p>
        The parties are each entitled to terminate this Agreement for cause at
        any time.
      </p>

      <p>
        You will not be entitled to any kind of compensation if you request to
        cancel or terminate the Service and will continue to be liable for any
        remaining payment obligations to OfferZen.
      </p>

      <h4 id="non-circumvention" data-level="2" className={styles.subSubHeading}>Non-Circumvention</h4>

      <p>
        You agree not to circumvent or attempt to circumvent OfferZen by
        communicating with and hiring a Candidate after you’ve Identified the
        Candidate on the Platform, outside of OfferZen’s process as set out in
        the Agreement. If you do this, there will be a fee of up to a maximum of
        12.5% (twelve point five percent) excluding VAT, of the Candidate’s
        Salary and your access to OfferZen may be suspended or denied.
      </p>

      <h4 id="privacy" data-level="2" className={styles.subSubHeading}>Privacy</h4>

      <p>
        Any personal data you share with us including our interactions with you will be treated in accordance with our Privacy Policy{' '}
        which can be accessed on our Website{' '}
        (<a href="/privacy" target="_blank" rel="noreferrer">www.offerzen.com/privacy</a>){' '}
        and will be updated from time to time as required. All questions around OfferZen’s Privacy Policy or your personal data{' '}
        should be directed to{' '}
        <a href="mailto:privacy@offerzen.com" target="_blank" rel="noreferrer">privacy@offerzen.com</a>.
      </p>

      <h4 id="confidentiality" data-level="2" className={styles.subSubHeading}>Confidentiality</h4>

      <p>You agree to keep all information gained from using OfferZen and/or any Service confidential.</p>

      <p>
        You agree not to publish or disclose to third parties, the names and/or identities of the Candidates whose profiles have been{' '}
        viewed by you on the Platform. You also agree to keep information about Interview Requests confidential. You undertake to{' '}
        implement and maintain all necessary physical, technical and administrative measures to protect the content obtained through{' '}
        the Platform against loss, improper use, unauthorised access, disclosure, alteration or destruction.
      </p>

      <p>
        You agree to keep your login details secret and not to divulge them in any form to any person or, in the{' '}
        case of company members, to any other company member.
      </p>

      <h4 id="licence-and-ownership" data-level="2" className={styles.subSubHeading}>Licence and Ownership</h4>

      <p>
        You own all the data and content you provide to us to use the Service and upload to the Platform. In order for us to provide{' '}
        the Service to you, you grant us a worldwide, irrevocable, perpetual, free of charge right and licence to access,{' '}
        store, host, modify, transmit and otherwise use your content and data as required to provide the Service and make it{' '}
        available to third parties eg. potential Candidates. You are solely responsible for securing and backing up your{' '}
        data and content.
      </p>

      <p>
        OfferZen owns all right, title and interest in the Service, all information, material, or content provided by OfferZen{' '}
        related to the Service or contained within the Service, and any update, adaptation, translation, customization or{' '}
        derivative work thereof, and all intellectual property rights in the Service, which shall remain with OfferZen and its{' '}
        licensors. OfferZen expressly reserves all rights in the Service and you shall obtain no ownership or other rights in{' '}
        the Service or any part thereof by virtue of your use.
      </p>

      <p>
        OfferZen grants you a non-exclusive, non-transferable licence for the
        duration of these Terms to access, use, and permit authorised
        representatives of your company to access and use the Service, in
        accordance with and subject to the terms and conditions of the
        Agreement. The Employer may not sublicence its rights other than to
        allow its authorised employees or representatives to access and use the
        Service for its own benefit.
      </p>

      <h4 id="indemnification" data-level="2" className={styles.subSubHeading}>Indemnification</h4>
      <p className={styles.noMargin}>
        You agree to indemnify, defend and hold harmless OfferZen, its employees, officers, directors, affiliates, successors and{' '}
        permitted assigns (“<b>OfferZen Parties</b>”) from and against any and all claims, damages, obligations, losses, liabilities,{' '}
        costs or debt, and expenses (including but not limited to attorney’s fees) which{' '}
        arise out of or relate to any claim or cause of action brought or asserted by third party:
      </p>
      <ul className={styles.noMargin}>
        <li>relating to your breach of these Terms, your use of the Service or the
          use of the Service by any person using your account
        </li>
        <li>alleging your negligent or more culpable acts or omissions</li>
        <li>
          alleging that the data you provided to us to perform the Services
          infringes the rights of or causes harm to any third party
        </li>
        <li>relating to any failure by you to comply with applicable laws and regulations</li>
      </ul>

      <h4 id="disclaimer" data-level="2" className={styles.subSubHeading}>Disclaimer</h4>

      <p>
        The Service is provided on an “as is” and “as available” basis. The
        Service is provided without warranties of any kind, whether express or
        implied, including, but not limited to, implied warranties of
        merchantability, fitness for a particular purpose, non-infringement or
        course of performance.
      </p>

      <h4 id="applicable-law-and-jurisdiction" data-level="2" className={styles.subSubHeading}>Applicable Law and Jurisdiction</h4>

      <p className={styles.noMargin}>
        These Terms and any dispute, controversy, proceedings or claim of whatever nature arising out of or in any way relating to{' '}
        these Terms or their formation (including any non-contractual disputes or claims) shall be governed by and construed in{' '}
        accordance with the laws of:
      </p>
      <ul className={styles.noMargin}>
        <li>the Netherlands and you agree to the courts in Amsterdam having exclusive jurisdiction; or</li>
        <li>
          the Republic of South Africa, if you are based in South Africa, and you agree to the{' '}
          courts in Cape Town having exclusive jurisdiction
        </li>
      </ul>

      <p>
        We may amend these Terms from time to time on notice to you. By using the Service you agree to be bound to{' '}
        the version of our Terms published here at the time of any visit to this Website.
      </p>
    </section>
  </>
)

export default TermsOfService
